import cn from 'classnames'
import React, { useState } from 'react'
import { Button } from 'primereact/button'
import styled from 'styled-components'

import Toggle from '../../common/components/Toggle'
import { notifyError, notifySuccess } from '../../common/features/notificationsManager/Notify'
import { ChargePoint, Connector, useChargePointRemoteStartMutation } from '../../common/redux/api/web-api'
import Text from '../../ui/Text'
import { isSupportDomain } from '../currentDomain'
import { RemoteControlRow } from '../remote-control-row'
import { SToggleButton } from '../toggle-button'
import { isLeftConnector } from '../findLeftConnector'
import { SelectRfidTagDialog } from '../../common/dialogs/select-rfid-tag-dialog'
import useText from '../../common/hooks/useText'

export type RemoteCapiNfcControlProps = {
    chargePoint: ChargePoint
    selectedConnector: Connector | null
    isReadOnly: boolean
    applyChargePoint: (chargePoint: ChargePoint) => Promise<void>
}

const SNfcDisabledIcon = styled.span`
    color: #888888;
    font-size: 21px;
`

const SNfcDisabledText = styled.span`
    font-size: 13px;
    font-weight: var(--font-weight-extra-bold);
    text-transform: uppercase;
    color: #888888;
`

export function RemoteCapiNfcControl({
    chargePoint,
    selectedConnector,
    isReadOnly,
    applyChargePoint,
}: RemoteCapiNfcControlProps): React.ReactElement | null {
    const text = useText()
    const toggleNfcStatus = async () => {
        await applyChargePoint({
            connectors: chargePoint?.connectors?.map((connector) => {
                if (connector.connectorId !== selectedConnector?.connectorId) return connector
                return { ...connector, nfcLocked: !connector.nfcLocked }
            }),
            id: chargePoint.id,
        })
    }
    const [remoteStart] = useChargePointRemoteStartMutation()

    const [selectRfidDialogIsOpen, setSelectRfidDialogIsOpen] = useState(false)

    const handleRemoteStart = async (rfidTag?: string) => {
        await remoteStart({
            chargePointId: chargePoint.id,
            connectorId: selectedConnector?.connectorId ?? 1,
            rfidTag,
        })
            .unwrap()
            .then(() => notifySuccess(text('msgRemoteStartSuccess')))
            .catch(() => notifyError(text('msgRemoteStartFailed')))
    }

    const handleRemoteControl = async () => {
        if (selectedConnector?.nfcLocked) {
            setSelectRfidDialogIsOpen(true)
        } else if (selectedConnector && selectedConnector.defaultNfcTagId) {
            handleRemoteStart(selectedConnector.defaultNfcTagId)
        } else {
            notifyError(text('msgRemoteStartFailed'))
        }
    }

    return (
        <>
            <RemoteControlRow
                title={
                    <div
                        data-testid={
                            selectedConnector
                                ? `rfid-${chargePoint.id}-connector-${selectedConnector.connectorId}`
                                : `rfid-${chargePoint.id}`
                        }
                    >
                        <Text code="lblRFIDLock" />
                    </div>
                }
                button={
                    chargePoint.nfcEnabled ? (
                        <Toggle
                            isToggled={!!selectedConnector?.nfcLocked}
                            disabled={isReadOnly}
                            toggleFn={toggleNfcStatus}
                            data-testid={
                                selectedConnector
                                    ? `nfc-${chargePoint.id}-connector-${selectedConnector.connectorId}`
                                    : `nfc-${chargePoint.id}`
                            }
                        />
                    ) : (
                        <SToggleButton
                            disabled
                            data-testid={
                                selectedConnector
                                    ? `nfcDisabled-${chargePoint.id}-connector-${selectedConnector.connectorId}`
                                    : `nfcDisabled-${chargePoint.id}`
                            }
                        >
                            <SNfcDisabledIcon className={cn('fa fa-toggle-off')} />
                            <SNfcDisabledText>
                                <Text code={'btnOff'} />
                            </SNfcDisabledText>
                        </SToggleButton>
                    )
                }
                status={
                    isSupportDomain &&
                    selectedConnector && (
                        <Button
                            type="button"
                            onClick={handleRemoteControl}
                            label={text('btnRFIDStart')}
                            icon="pi pi-play-circle"
                            data-testid={`rfid-${
                                isLeftConnector(selectedConnector?.connectorId) ? 'left' : 'right'
                            }-btn`}
                        />
                    )
                }
            />
            <SelectRfidTagDialog
                visible={selectRfidDialogIsOpen}
                chargePointId={chargePoint.id}
                connectorId={selectedConnector?.connectorId ?? 1}
                onStartCharging={(rfidTag) => {
                    handleRemoteStart(rfidTag)
                    setSelectRfidDialogIsOpen(false)
                }}
                onHide={() => setSelectRfidDialogIsOpen(false)}
            />
        </>
    )
}
